import { filter, forEach, fromEntries, isNumber, map, pipe } from 'remeda'
import type { Cleanup } from '../helpers/cleanup'
import { bindLiveProps, type LivePropValues } from '../helpers/live-props'
import { setupPageComponent } from '../helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type SliderElement = HTMLElement & {
  readonly range: boolean
  value: number
  minValue: number
  maxValue: number
  readonly updateComplete: Promise<boolean>
}

type SliderHookContext = {
  readonly el: HTMLElement
  _cleanup?: Cleanup
}

type SliderValueProperty = 'value' | 'minValue' | 'maxValue'

// Constants -------------------------------------------------------------------

const SLIDER_VALUE_PROPERTIES = [
  'value',
  'minValue',
  'maxValue'
] as const satisfies readonly SliderValueProperty[]

// Private functions -----------------------------------------------------------

// Narrows the hook host to Web Awesome's slider API.
const isSliderElement = (element: HTMLElement): element is SliderElement =>
  element.tagName === 'WA-SLIDER' &&
  'range' in element &&
  'value' in element &&
  'minValue' in element &&
  'maxValue' in element &&
  'updateComplete' in element

// Narrows one form carrier to a hidden native input.
const isHiddenInput = (element: Element | null): element is HTMLInputElement =>
  element instanceof HTMLInputElement && element.type === 'hidden'

// Writes one numeric value into a hidden form carrier.
const setHiddenValue = (element: Element | null, value: number): void => {
  if (isHiddenInput(element)) {
    element.value = String(value)
  }
}

// Mirrors the scalar or range values into their native form carriers.
const syncFormValues = (element: SliderElement): void => {
  if (element.range) {
    setHiddenValue(
      element.querySelector('[data-slider-min-value]'),
      element.minValue
    )
    setHiddenValue(
      element.querySelector('[data-slider-max-value]'),
      element.maxValue
    )
    return
  }

  setHiddenValue(element.querySelector('[data-slider-value]'), element.value)
}

// Reads the requested finite slider properties as one atomic value group.
const readValues = (
  element: SliderElement,
  propertyNames: readonly string[]
): LivePropValues =>
  fromEntries(
    pipe(
      SLIDER_VALUE_PROPERTIES,
      filter((property) => propertyNames.includes(property)),
      map((property) => [property, element[property]] as const),
      filter((entry) => Number.isFinite(entry[1]))
    )
  )

// Applies one finite numeric property when the contract contains it.
const applyFiniteValue = (
  element: SliderElement,
  property: SliderValueProperty,
  value: unknown
): void => {
  if (isNumber(value) && Number.isFinite(value)) {
    element[property] = value
  }
}

// Applies one atomic slider group and then synchronizes its form values.
const applyValues = async (
  element: SliderElement,
  values: LivePropValues,
  signal: AbortSignal
): Promise<void> => {
  forEach(SLIDER_VALUE_PROPERTIES, (property) =>
    applyFiniteValue(element, property, values[property])
  )

  await element.updateComplete

  if (!signal.aborted) {
    syncFormValues(element)
  }
}

// Connects property and form synchronization for one slider host.
const setupSlider = (element: HTMLElement): Cleanup => {
  if (!isSliderElement(element)) {
    return () => undefined
  }

  const handleInput = (): void => syncFormValues(element)
  const cleanupLiveProps = bindLiveProps(element, {
    read: (propertyNames) => readValues(element, propertyNames),
    apply: (values, signal) => applyValues(element, values, signal)
  })

  element.addEventListener('input', handleInput)

  return () => {
    cleanupLiveProps()
    element.removeEventListener('input', handleInput)
  }
}

// Public hook -----------------------------------------------------------------

export const FacadeSlider = {
  // Connects live props once the slider is ready.
  mounted(this: SliderHookContext) {
    this._cleanup = setupPageComponent(this.el, setupSlider)
  },

  // Releases listeners and pending live-props work.
  destroyed(this: SliderHookContext) {
    this._cleanup?.()
    this._cleanup = undefined
  }
}
